import HiddenInput from "@/components/form/components/HiddenInput";
import { Injector } from "@/components/form/Injector";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { selectStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { getActiveEnvProfile } from "@/utils/utils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { Checkbox, Col, Form, Input, Modal, Row, Select, Space } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import { useCreateCommissionsBatch, useGetCommissionsBatchNamePrefix } from "../../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { CommissionsBatchType } from "../../enums";
import { COMMISSIONS_BATCH_ROUTE_PATHS } from "../../paths";
import { CreateCommissionsBatch } from "../../types";
import { isCommissionAdvanceBatch } from "../../utils";

const isNamePrefixEditable = getActiveEnvProfile() === "dev" || getActiveEnvProfile() === "test";

type Props = {
  open: boolean;
  onFormCancel: () => void;
};

export const CreateCommissionsBatchForm = ({ open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const { data: namePrefix } = useGetCommissionsBatchNamePrefix(open);

  const createCommissionsBatch = useCreateCommissionsBatch();

  const [form] = Form.useForm<CreateCommissionsBatch>();
  const name = Form.useWatch("name", form);
  const type = Form.useWatch("type", form);

  const closesCommissionAdvanceMonthByDefault = !!namePrefix?.endsWith("-03");

  useEffect(() => {
    if (open && namePrefix && !name) {
      form.setFieldsValue({ name: namePrefix });
    }
  }, [open, namePrefix, form]);

  const handleFormSubmit = () => {
    form.validateFields().then((values) =>
      createCommissionsBatch.mutate(values, {
        onError: (error) => setErrorsToForm(form, "commissions.batch.attrs", error.violations),
        onSuccess: (data) => {
          navigate(generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.detail.to, { id: data.id }));
          onFormCancel();
        },
      }),
    );
  };

  return (
    <Modal
      width={ModalSizes.SMALL}
      open={open}
      title={t("commissions.batch.titles.createBatch")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      afterClose={() => form.resetFields()}
      confirmLoading={createCommissionsBatch.isPending}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="createCommissionsBatchForm">
        <Row gutter={rowGutter}>
          <Col span={24}>
            {isNamePrefixEditable ? (
              <Form.Item
                name="name"
                label={t("commissions.batch.attrs.name")}
                rules={[
                  validations.size(1, 64),
                  validations.notBlank,
                  validations.pattern(regexPatterns.commissionsBatchNameRegex),
                ]}
              >
                <Input />
              </Form.Item>
            ) : (
              <Form.Item
                name="name"
                label={t("commissions.batch.attrs.name")}
                rules={[validations.size(1, 64), validations.notBlank]}
                normalize={(value) => [namePrefix, value ?? ""].join("-")}
                getValueProps={(value) => ({ value: value?.substring(11) })}
              >
                <Injector>
                  {(props) => (
                    <Space.Compact block>
                      <Space.Addon className="nowrap">{namePrefix}</Space.Addon>

                      <Input placeholder={t("commissions.batch.helpers.batchNamePlaceholder")} {...props} />
                    </Space.Compact>
                  )}
                </Injector>
              </Form.Item>
            )}
          </Col>
        </Row>

        {isTopAgentPlus ? (
          <>
            <Row gutter={rowGutter}>
              <Col span={24}>
                <Form.Item
                  name="type"
                  label={t("commissions.batch.enums.commissionBatchType_PLUS._label")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    options={Object.values(CommissionsBatchType).map((type) => ({
                      value: type,
                      label: t(`commissions.batch.enums.commissionBatchType_PLUS.${type}`),
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            {isCommissionAdvanceBatch(type, isTopAgentPlus) && (
              <Row gutter={rowGutter}>
                <Col span={24}>
                  <Form.Item
                    name="closesCommissionAdvanceMonth"
                    valuePropName="checked"
                    rules={[validations.none]}
                    initialValue={closesCommissionAdvanceMonthByDefault}
                    preserve={false}
                  >
                    <Checkbox>
                      <LabelWithTooltip
                        label={t("commissions.batch.attrs.closesCommissionAdvanceMonth")}
                        tooltip={t("commissions.batch.helpers.closesCommissionAdvanceMonthDesc")}
                      />
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            )}
          </>
        ) : (
          <HiddenInput name="type" initialValue={CommissionsBatchType.PRIMARY} />
        )}
      </Form>
    </Modal>
  );
};
